import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";

const TRUST = [
  { stat: "5,000+", label: "SAT practice questions" },
  { stat: "2,500+", label: "ACT practice questions" },
  { stat: "97%",    label: "of students improve" },
];

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      window.location.href = "/dashboard";
    } catch (err: any) {
      setError(err.message || "Failed to sign in. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Left panel ── */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-[#070f24] via-[#0d2248] to-[#1a3a6b] flex-col justify-between p-12 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-[#f5a623]/10 blur-3xl pointer-events-none" />

        <a href="/">
          <img
            src="/logo.svg"
            alt="Scholar Sphere"
            className="h-11 w-auto brightness-0 invert"
          />
        </a>

        <div>
          <h1 className="text-4xl font-black text-white leading-tight mb-4">
            Welcome back.<br />
            <span className="text-[#f5a623]">Keep practicing.</span>
          </h1>
          <p className="text-white/60 text-base leading-relaxed mb-10">
            Your question bank, review sessions, and AI assistant are waiting for you.
          </p>
          <ul className="space-y-4">
            {[
              "5,000+ SAT and ACT practice questions",
              "Full review mode — learn from every mistake",
              "Personalized AI assistant on every question",
              "Expert tutors available for live 1:1 sessions",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-white/80">
                <span className="w-5 h-5 rounded-full bg-[#f5a623]/20 border border-[#f5a623]/40 flex items-center justify-center flex-shrink-0 text-[#f5a623] text-xs font-bold">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Stats strip */}
        <div className="flex gap-8 pt-8 border-t border-white/10">
          {TRUST.map(({ stat, label }) => (
            <div key={label}>
              <div className="text-2xl font-black text-[#f5a623]">{stat}</div>
              <div className="text-xs text-white/50 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 bg-white flex flex-col items-center justify-center px-6 py-12">
        {/* Mobile logo */}
        <a href="/" className="mb-8 md:hidden">
          <img src="/logo.svg" alt="Scholar Sphere" className="h-10" />
        </a>

        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-extrabold text-[#1a2435] mb-1">Sign in</h2>
          <p className="text-[#5a6a7e] text-sm mb-7">Enter your details to access your account.</p>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-[#1a2435] mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6a7e]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-2.5 border border-[#dde3ed] rounded-xl text-sm text-[#1a2435] placeholder:text-[#5a6a7e]/60 focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b] transition-colors bg-[#f8f9fc]"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-[#1a2435]">Password</label>
                <a href="/forgot-password" className="text-xs text-[#f5a623] font-semibold hover:underline">
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6a7e]" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="w-full pl-10 pr-10 py-2.5 border border-[#dde3ed] rounded-xl text-sm text-[#1a2435] placeholder:text-[#5a6a7e]/60 focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b] transition-colors bg-[#f8f9fc]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a6a7e] hover:text-[#1a2435] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#1a3a6b] hover:bg-[#12265a] text-white font-bold py-2.5 rounded-xl transition-colors text-sm disabled:opacity-60"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="text-center text-sm text-[#5a6a7e] mt-6">
            Don't have an account?{" "}
            <a href="/signup" className="text-[#1a3a6b] font-semibold hover:underline">Sign up free</a>
          </p>
        </div>
      </div>
    </div>
  );
}
